import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
  {
    question: "Who can join Ethiccraft?",
    answer: "Any student or youth eager to grow, learn values, and serve society is welcome to join Ethiccraft."
  },
  {
    question: "How can I contact you?",
    answer: "You can reach us via email at info@ethiccraft.org or message us on Instagram, WhatsApp, or through our website form."
  },
  {
    question: "How do I participate in events?",
    answer: "Follow us on social media or check the website for upcoming events. Registration links are posted for each event."
  },
  {
    question: "Is there any fee to join or attend?",
    answer: "Most activities are free. If there's any fee, it will be mentioned in the event details."
  },
  {
    question: "Do I get a certificate for joining?",
    answer: "Yes, certificates are provided for many workshops, webinars, and official training events."
  },
  {
    question: "Are there online events too?",
    answer: "Yes! We host webinars, virtual courses, and online youth conferences open to all."
  }
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 } // Adjust threshold if needed for when animation triggers
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      ref={sectionRef}
      initial={{ opacity: 0, y: 50 }} // Animate from slightly below
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="faq-container"
    >
      <div className="faq-left">
        <h1>Frequently Asked Questions</h1>
        <p>
          Didn’t find what you were looking for? Our team is here to help. Reach out to us through any of the following ways:
        </p>
        <ul className="contact-list">
          <li>
            📧 Email us at <a href="mailto:info@ethiccraft.org">info@ethiccraft.org</a>
          </li>
          <li>
            💬 Chat with us on WhatsApp at <a href="https://wa.me/918860830455" target="_blank" rel="noopener noreferrer">+91 8860830455</a>
          </li>
          <li>
            📱 DM us on Instagram: <a href="https://instagram.com/ethiccraft_org" target="_blank" rel="noopener noreferrer">@ethiccraft_org</a>
          </li>
        </ul>
        <p>
          We usually respond within a few hours during working days.
        </p>
      </div>


      <div className="faq-right">
        {faqs.map((faq, index) => (
          <div key={index} className="faq-item" onClick={() => toggleFAQ(index)}>
            <motion.div
              className="faq-question"
              // Removed whileHover from here, will handle hover with CSS
            >
              <span>{faq.question}</span>
              <motion.div
                animate={{ rotate: openIndex === index ? 180 : 0 }}
                transition={{ duration: 0.3, ease: "easeInOut", type: "spring", stiffness: 150 }}
                className=""
              >
                &#x2304; {/* Unicode for a cleaner down arrow */}
              </motion.div>
            </motion.div>

            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginTop: 0, paddingBottom: 0 }} // Reset specific properties for collapse
                  animate={{ opacity: 1, height: "auto", marginTop: "5px", paddingBottom: "15px" }} // Animate into view
                  exit={{ opacity: 0, height: 0, marginTop: 0, paddingBottom: 0 }} // Animate out of view
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="faq-answer"
                >
                  {faq.answer}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </motion.div>
  );
}